'use client';

import React, { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import HumanSupportChat from '@/components/chat/HumanSupportChat';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type AiMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

const welcomeMessage: AiMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    'Xin chào! Tôi là DN TECH AI. Bạn muốn tư vấn linh kiện, chọn cấu hình PC hay cần hỗ trợ đặt hàng?',
};

const quickPrompts = [
  'Tư vấn cấu hình PC gaming 20 triệu',
  'Nên chọn CPU Intel hay AMD?',
  'Gợi ý nâng cấp máy tính',
];

function createMessage(role: AiMessage['role'], content: string): AiMessage {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    content,
  };
}

export default function ChatWidget() {
  const pathname = usePathname();
  const [supportSettingsLoaded, setSupportSettingsLoaded] = useState(false);
  const [quickSupportEnabled, setQuickSupportEnabled] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'ai' | 'human'>('ai');
  const [messages, setMessages] = useState<AiMessage[]>([welcomeMessage]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const chatDisabled =
    !supportSettingsLoaded ||
    !quickSupportEnabled ||
    pathname?.startsWith('/admin') ||
    pathname?.startsWith('/khoanh');

  useEffect(() => {
    fetch('/api/settings/public', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        const supportConfig = payload?.settings?.support_config;
        setQuickSupportEnabled(supportConfig?.quickSupportEnabled !== false);
      })
      .catch(() => setQuickSupportEnabled(true))
      .finally(() => {
        setSupportSettingsLoaded(true);
        setMounted(true);
      });
  }, []);

  useEffect(() => {
    if (chatDisabled) return;

    const openChatFromExternalTrigger = () => setIsOpen(true);
    window.addEventListener('dntech:open-chat', openChatFromExternalTrigger as EventListener);
    return () => {
      window.removeEventListener('dntech:open-chat', openChatFromExternalTrigger as EventListener);
    };
  }, [chatDisabled]);

  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    inputRef.current?.focus();
  }, [isOpen, messages, isLoading]);

  const resetChat = () => {
    setMessages([welcomeMessage]);
    setInput('');
    setError(null);
    setIsLoading(false);
  };

  const sendMessage = async (value?: string) => {
    const content = (value ?? input).trim();
    if (!content || isLoading) return;

    const userMessage = createMessage('user', content.slice(0, 4000));
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: nextMessages.map(({ role, content: messageContent }) => ({
            role,
            content: messageContent,
          })),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Chatbot AI đang bận. Vui lòng thử lại.');

      setMessages((current) => [...current, createMessage('assistant', String(payload.message))]);
    } catch (requestError) {
      const message =
        requestError instanceof Error ? requestError.message : 'Không thể kết nối Chatbot AI.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void sendMessage();
  };

  if (!mounted || chatDisabled) return null;

  return (
    <>
      {isOpen ? (
        <button
          type="button"
          aria-label="Đóng Chatbot AI"
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-[9990] bg-slate-950/55"
        />
      ) : null}

      <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end sm:bottom-8 sm:right-8">
        {isOpen ? (
          <section
            role="dialog"
            aria-modal="true"
            aria-label="Chatbot AI DN TECH"
            className="mb-4 flex h-[min(680px,calc(100vh-110px))] w-[min(520px,calc(100vw-32px))] flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl ring-1 ring-slate-900/10"
          >
            <header className="flex shrink-0 items-center justify-between bg-yellow-400 px-5 py-4 text-slate-950 sm:px-6 sm:py-5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white shadow-md">
                  <SparklesIcon className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-black leading-tight sm:text-xl">
                    {mode === 'ai' ? 'Chatbot AI' : 'Chat với nhân viên'}
                  </h2>
                  <p className="mt-0.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-800/75">
                    DN TECH AI
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {mode === 'ai' ? (
                  <button
                    type="button"
                    onClick={() => setMode('human')}
                    className="mr-1 inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-2 text-[11px] font-bold text-white shadow-sm hover:bg-slate-800 sm:px-3 sm:text-xs"
                  >
                    <UserGroupIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Chat nhân viên</span>
                    <span className="sm:hidden">Nhân viên</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  aria-label="Bắt đầu cuộc trò chuyện mới"
                  title="Cuộc trò chuyện mới"
                  onClick={() => {
                    setMode('ai');
                    resetChat();
                  }}
                  className="rounded-full p-2 text-slate-800 transition-colors hover:bg-black/10"
                >
                  <ArrowPathIcon className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  aria-label="Thu nhỏ Chatbot AI"
                  onClick={() => setIsOpen(false)}
                  className="rounded-full p-2 text-slate-800 transition-colors hover:bg-black/10"
                >
                  <ChevronDownIcon className="h-6 w-6" />
                </button>
              </div>
            </header>

            {mode === 'human' ? (
              <HumanSupportChat onBack={() => setMode('ai')} />
            ) : (
            <div className="flex min-h-0 flex-1 flex-col bg-slate-50">
              <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm sm:text-[15px] ${
                          message.role === 'user'
                            ? 'rounded-br-md bg-yellow-400 text-slate-950'
                            : 'rounded-bl-md border border-slate-200 bg-white text-slate-700'
                        }`}
                      >
                        {message.content}
                      </div>
                    </div>
                  ))}

                  {isLoading ? (
                    <div className="flex justify-start">
                      <div className="rounded-2xl rounded-bl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
                        <div className="flex items-center gap-1.5" aria-label="Chatbot AI đang trả lời">
                          <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.2s]" />
                          <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.1s]" />
                          <span className="h-2 w-2 animate-bounce rounded-full bg-slate-400" />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {messages.length === 1 && !isLoading ? (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {quickPrompts.map((prompt) => (
                        <button
                          type="button"
                          key={prompt}
                          onClick={() => void sendMessage(prompt)}
                          className="rounded-full border border-yellow-300 bg-yellow-50 px-3 py-2 text-left text-xs font-semibold text-slate-700 transition-colors hover:bg-yellow-100"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {error ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                      {error}
                    </div>
                  ) : null}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              <div className="shrink-0 border-t border-slate-200 bg-white px-4 pb-4 pt-3 sm:px-5">
                <form onSubmit={handleSubmit} className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    maxLength={4000}
                    onChange={(event) => setInput(event.target.value)}
                    placeholder="Hỏi Chatbot AI..."
                    aria-label="Nhập câu hỏi cho Chatbot AI"
                    className="min-w-0 flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-yellow-400 focus:bg-white focus:ring-4 focus:ring-yellow-400/15"
                  />
                  <button
                    type="submit"
                    aria-label="Gửi câu hỏi"
                    disabled={!input.trim() || isLoading}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-yellow-400 text-slate-950 shadow-sm transition-[background-color,transform,opacity] hover:bg-yellow-500 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <PaperAirplaneIcon className="h-5 w-5" />
                  </button>
                </form>
                <p className="mt-2 text-center text-[10px] font-medium text-slate-400">
                  Câu trả lời AI dùng để tham khảo. Giá và tồn kho xem tại sản phẩm trên website.
                </p>
              </div>
            </div>
            )}
          </section>
        ) : null}

        <button
          type="button"
          aria-label={isOpen ? 'Đóng Chatbot AI' : 'Mở Chatbot AI'}
          title="Chatbot AI DN TECH"
          onClick={() => setIsOpen((open) => !open)}
          className={`relative flex h-14 w-14 items-center justify-center rounded-full border-4 border-white shadow-xl transition-[transform,background-color] duration-200 sm:h-16 sm:w-16 ${
            isOpen
              ? 'rotate-90 bg-white text-slate-400'
              : 'bg-yellow-400 text-slate-950 hover:scale-105 active:scale-95'
          }`}
        >
          {isOpen ? (
            <XMarkIcon className="h-7 w-7" />
          ) : (
            <>
              <ChatBubbleLeftRightIcon className="h-7 w-7" />
              <span className="absolute right-1 top-1 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
            </>
          )}
        </button>
      </div>
    </>
  );
}
